import type { Lang } from '@fonus/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, KeyRound, Mic2, PencilLine } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Confirm, Field, inputClass, ListSkeleton, PageTitle } from '../components/common.tsx';
import { ErrorCard } from '../components/ErrorCard.tsx';
import { Badge } from '../components/ui/badge.tsx';
import { Button } from '../components/ui/button.tsx';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card.tsx';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '../components/ui/dialog.tsx';
import { Progress } from '../components/ui/progress.tsx';
import { api } from '../lib/api.ts';
import { humanFromApi, humanFromJob } from '../lib/errors.ts';
import { credits, LANG_LABEL } from '../lib/format.ts';
import { useReadiness, useVoices } from '../lib/queries.ts';
import { isStaleError } from '../lib/stale.ts';
import { cn, newKey } from '../lib/utils.ts';

const LANGS: Lang[] = ['uz', 'ru', 'en'];

function ManualVoice({
  lang,
  open,
  onOpenChange,
  onSaved,
}: {
  lang: Lang;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onSaved: () => void;
}) {
  const [voiceId, setVoiceId] = useState('');
  const [modelId, setModelId] = useState<'eleven_v3' | 'eleven_v4' | 'eleven_multilingual_v2'>(
    'eleven_v3',
  );
  const save = useMutation({
    mutationFn: () =>
      api('/admin/voices/manual', {
        method: 'PUT',
        body: { lang, voiceId: voiceId.trim(), modelId },
      }),
    onSuccess: () => {
      toast.success(`Голос для ${lang} сохранён`);
      onOpenChange(false);
      onSaved();
    },
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogTitle>Вставить ID голоса вручную · {LANG_LABEL[lang]}</DialogTitle>
        <DialogDescription>
          Если подходящего голоса нет среди кандидатов — скопируйте его ID в ElevenLabs (Voices →
          голос → «ID»).
        </DialogDescription>
        <div className="mt-4 flex flex-col gap-3">
          <Field label="ID голоса">
            <input
              className={inputClass}
              value={voiceId}
              placeholder="например, 21m00Tcm4TlvDq8ikWAM"
              onChange={(e) => setVoiceId(e.target.value)}
            />
          </Field>
          <Field
            label="Модель"
            hint={lang === 'uz' ? 'Для узбекского — только v4 или v3.' : undefined}
          >
            <select
              className={inputClass}
              value={modelId}
              onChange={(e) => setModelId(e.target.value as typeof modelId)}
            >
              <option value="eleven_v3">Eleven v3</option>
              <option value="eleven_v4">Eleven v4</option>
              {lang !== 'uz' && <option value="eleven_multilingual_v2">Multilingual v2</option>}
            </select>
          </Field>
          {save.error && <ErrorCard error={humanFromApi(save.error)} />}
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Отмена
          </Button>
          <Button
            disabled={voiceId.trim().length < 10 || save.isPending}
            onClick={() => save.mutate()}
          >
            Сохранить
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function Voices({ isOwner }: { isOwner: boolean }) {
  const q = useVoices();
  const readiness = useReadiness();
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState(false);
  const [manual, setManual] = useState<Lang | null>(null);
  const [key, setKey] = useState(newKey);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['voices'] });
    void qc.invalidateQueries({ queryKey: ['readiness'] });
  };
  const audition = useMutation({
    mutationFn: () =>
      api('/admin/voices/audition', { method: 'POST', body: { idempotencyKey: key } }),
    onSuccess: () => {
      toast.success('Подбор запущен');
      setConfirm(false);
      setKey(newKey());
      refresh();
    },
  });
  const choose = useMutation({
    mutationFn: (sampleId: string) => api('/admin/voices', { method: 'PUT', body: { sampleId } }),
    onSuccess: () => {
      toast.success('Голос выбран');
      refresh();
    },
    onError: (e) => toast.error(humanFromApi(e).title),
  });

  if (q.isLoading || readiness.isLoading) return <ListSkeleton rows={4} />;
  if (!q.data || !readiness.data)
    return q.error ? <ErrorCard error={humanFromApi(q.error)} /> : null;
  const { selected, samples, lastJob, auditionCredits } = q.data;
  const keyStep = readiness.data.steps.find((s) => s.id === 'elevenlabs');
  const keyOk = keyStep?.status === 'done';
  const running = lastJob?.status === 'QUEUED' || lastJob?.status === 'RUNNING';
  const out = (lastJob?.output ?? {}) as { done?: number; total?: number };
  const prov = readiness.data.credits.provider;
  const remaining = prov ? prov.limit - prov.used : null;

  return (
    <div className="flex flex-col gap-5">
      <PageTitle
        title="Голоса"
        subtitle="По 3 тёплых спокойных голоса на язык. Один отрывок ≈300 символов каждым голосом — послушайте и выберите."
      />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {keyOk ? (
              <CheckCircle2 className="size-5 text-ok" />
            ) : (
              <KeyRound className="size-5 text-muted" />
            )}{' '}
            1. Ключ ElevenLabs
          </CardTitle>
          <CardDescription>
            {keyOk
              ? 'Ключ работает, права на чтение голосов и баланса есть.'
              : 'Сначала подключите ключ с правами Text to Speech, Voices: Read и User: Read.'}
          </CardDescription>
        </CardHeader>
        {!keyOk && (
          <CardContent>
            <Button asChild>
              <a href="#/">Исправить на главной</a>
            </Button>
          </CardContent>
        )}
      </Card>

      {keyOk && (
        <Card>
          <CardHeader>
            <CardTitle>2. Подбор голосов</CardTitle>
            <CardDescription>
              Готовые отрывки сохраняются: повторный подбор бесплатен для уже озвученных кандидатов.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            {isOwner ? (
              <div>
                <Button
                  size="lg"
                  disabled={running || audition.isPending || auditionCredits === 0}
                  onClick={() => setConfirm(true)}
                >
                  <Mic2 />{' '}
                  {auditionCredits === 0
                    ? 'Все отрывки уже готовы'
                    : `Запустить подбор (≈${credits(auditionCredits)}${remaining !== null ? ` из ${credits(remaining)}` : ''})`}
                </Button>
              </div>
            ) : (
              <p className="text-[14px] text-muted">Запуск подбора — у владельца.</p>
            )}
            {running && (
              <div className="flex flex-col gap-1" aria-live="polite">
                <p className="text-[14px]">
                  Голос {out.done ?? 0} из {out.total ?? 9}…
                </p>
                <Progress value={out.total ? ((out.done ?? 0) / out.total) * 100 : 5} />
              </div>
            )}
            {lastJob?.status === 'FAILED' && !isStaleError(lastJob, readiness.data) && (
              <ErrorCard error={humanFromJob(lastJob)} />
            )}
            {audition.error && <ErrorCard error={humanFromApi(audition.error)} />}
          </CardContent>
        </Card>
      )}

      {keyOk &&
        LANGS.map((lang) => {
          const list = samples.filter((s) => s.lang === lang);
          const sel = selected[lang];
          return (
            <Card key={lang}>
              <CardHeader>
                <CardTitle className="flex flex-wrap items-center gap-2">
                  3. {LANG_LABEL[lang]}
                  {sel ? (
                    <Badge tone="ok">✓ {sel.voiceName}</Badge>
                  ) : (
                    <Badge tone="warn">голос не выбран</Badge>
                  )}
                </CardTitle>
                {lang === 'uz' && (
                  <CardDescription>
                    Слушайте произношение oʻ, gʻ, q, x и тутук белгиси.
                  </CardDescription>
                )}
              </CardHeader>
              <CardContent className="flex flex-col gap-3">
                {list.length === 0 ? (
                  <p className="text-[14px] text-muted">
                    {running ? 'Озвучиваем…' : 'Отрывков пока нет — запустите подбор.'}
                  </p>
                ) : (
                  <div className="grid gap-3 md:grid-cols-3">
                    {list.map((s) => {
                      const isSel = sel?.voiceId === s.voiceId && sel.modelId === s.modelId;
                      return (
                        <div
                          key={s.id}
                          className={cn(
                            'flex flex-col gap-2 rounded-[16px] border p-3',
                            isSel ? 'border-accent bg-accent-soft' : 'border-line',
                          )}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <strong>{s.voiceName}</strong>
                            {isSel && (
                              <CheckCircle2 className="size-5 text-accent" aria-label="Выбран" />
                            )}
                          </div>
                          <span className="text-[12px] text-muted">{s.modelId}</span>
                          <audio controls preload="none" src={s.url} className="w-full" />
                          {isOwner && (
                            <Button
                              size="sm"
                              variant={isSel ? 'secondary' : 'default'}
                              disabled={isSel || choose.isPending}
                              onClick={() => choose.mutate(s.id)}
                            >
                              {isSel ? 'Выбран' : 'Выбрать'}
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                {isOwner && (
                  <Button
                    variant="link"
                    size="sm"
                    className="self-start px-0"
                    onClick={() => setManual(lang)}
                  >
                    <PencilLine /> Вставить ID голоса вручную
                  </Button>
                )}
              </CardContent>
            </Card>
          );
        })}

      <Confirm
        open={confirm}
        onOpenChange={setConfirm}
        title="Запустить подбор голосов?"
        description={
          <>
            Будет потрачено до ≈{credits(auditionCredits)} ElevenLabs
            {remaining !== null ? ` (на счёте ${credits(remaining)})` : ''}.
          </>
        }
        confirmLabel="Запустить"
        busy={audition.isPending}
        onConfirm={() => audition.mutate()}
      />
      {manual && (
        <ManualVoice
          lang={manual}
          open
          onOpenChange={(o) => !o && setManual(null)}
          onSaved={refresh}
        />
      )}
    </div>
  );
}
